/**
 * Loader Component
 * iOS-style activity indicator - exact replica of iOS UIActivityIndicatorView
 * 
 * @author Varun Pandey
 */

import React from 'react';
import { cn } from '../../utils/cn';

export interface LoaderProps {
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
  className?: string;
  /** Optional text shown above the loader (e.g. "Logging out..."). Used with fullscreen overlay. */
  message?: string;
}

export const Loader: React.FC<LoaderProps> = ({ size = 'medium', className, message }) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-6 h-6',
    large: 'w-8 h-8',
    fullscreen: 'w-4 h-4', // small spinner on fullscreen overlay
  };

  const barCount = 10;

  // iOS activity indicator opacity values - adjusted for 10 bars
  // Bars are arranged clockwise, with opacity gradient from top (1.0) to bottom (0.1)
  const getOpacity = (index: number) => {
    // Bar 0 (top/12 o'clock) = 1.0, decreasing clockwise to bar 5 (bottom) = 0.15, then back up
    const opacities = [
      1.0,   // 0 - top
      0.85,  // 1
      0.7,   // 2
      0.55,  // 3
      0.4,   // 4
      0.2,   // 5 - bottom
      0.15,  // 6
      0.25,  // 7
      0.4,   // 8
      0.6,   // 9
    ];
    return opacities[index] || 0.1;
  };

  // Use white color for all sizes
  const fillColor = '#FFFFFF';

  const LoaderContent = () => (
    <div className={cn('relative flex items-center justify-center', sizeClasses[size], className)}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
        style={{
          animation: 'ios-spin 0.8s linear infinite',
        }}
      >
        <style>
          {`
            @keyframes ios-spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
        {Array.from({ length: barCount }, (_, i) => {
          const angle = (i * 360) / barCount;
          const opacity = getOpacity(i);

          return (
            <rect
              key={i}
              x="11"
              y="0.5"
              width="2"
              height="7"
              rx="1"
              ry="1"
              fill={fillColor}
              opacity={opacity}
              transform={`rotate(${angle} 12 12)`}
            />
          );
        })}
      </svg>
    </div>
  );

  if (size === 'fullscreen') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center backdrop-blur-sm bg-black/20">
        {message != null && message !== '' && (
          <p className="text-white text-sm font-medium mb-3">{message}</p>
        )}
        <LoaderContent />
      </div>
    );
  }

  return <LoaderContent />;
};
